import * as _ from 'lodash'
import { range } from 'lodash'
import {
  City,
  Country,
  Folder,
  User,
  Center,
  Hub,
  HubCourse,
  AppLocale,
  Program,
  Exam,
  SmsKind,
  SmsTemplate,
  CoursesGroup,
  Course,
  Briefing, CancellationReason
} from 'models'

export interface FormOption {
  label: string
  value: any
  disabled?: boolean
  group?: boolean
  subLabel?: string
  subOptions?: FormOption[]
}

export class FormOptionsBuilder {
  static folderOptions(folders: Folder[]): FormOption[] {
    return folders.map((folder: Folder) => {
      let option = { value: folder.id, label: folder.name }
      if(folder.subFolders.length) {
        option['subOptions'] = this.folderOptions(folder.subFolders)
      }
      return option
    })
  }

  static folderItemOptions(
    folders: Folder[],
    items: { [key: number]: any[] }|any[],
    valueAttr: string = 'id',
    labelAttr: string = 'name',
    subLabelAttr: string = 'comment',
    folderIdAttr: string = 'folderId',
    includeEmpty: boolean = false
  ): FormOption[] {
    let itemsIndex = _.isArray(items) ? _.groupBy(items, folderIdAttr) : items
    let result = []
    for (let folder of folders) {
      if (!this.folderSubtreeHasItems(folder, itemsIndex)) {
        continue
      }
      let group = { label: folder.name, group: true }
      if(folder.subFolders.length || (itemsIndex[folder.id] && itemsIndex[folder.id].length)) {
        group['subOptions'] = []
        if (folder.subFolders.length) {
          for (let subGroup of this.folderItemOptions(folder.subFolders, itemsIndex)) {
            group['subOptions'].push(subGroup)
          }
        }
        if(itemsIndex[folder.id] && itemsIndex[folder.id].length) {
          for(let item of itemsIndex[folder.id]) {
            group['subOptions'].push({
              value:    item[valueAttr],
              label:    item[labelAttr],
              subLabel: item[subLabelAttr]
            })
          }
        }
      }
      result.push(group)
    }

    if (includeEmpty) {
      result.unshift({
        value: null,
        label: "Не выбрано"
      })
    }
    return result
  }

  static courseOptions(groups: CoursesGroup[], courses: Course[]): FormOption[] {
    if (groups.length > 1) {
      return groups.map(group => ({
        value: group.id,
        label: group.name,
        group: true,
        subOptions: courses.filter(course => group.courses.indexOf(course.value) > -1 )
      }))
    } else if (groups.length === 1) {
      return courses.filter(course => groups[0].courses.indexOf(course.value) > -1 )
    } else {
      return []
    }

  }

  static cityOptions(countries: Country[], cities: City[]): FormOption[] {
    let result = []
    for (let country of countries) {
      let cityOptions = cities
        .filter((city: City) => city.countryId == country.id)
        .map((city: City) => ({ 
          value: city.id, 
          label: city.name 
        }))

      if (cityOptions.length > 0) {
        result.push({
          label: country.name,
          group: true,
          subOptions: cityOptions
        })
      }
    }
    return result
  }

  static appLocaleOptions(locales: AppLocale[]): FormOption[] {
    return locales.map(l => {
      let label = `${l.name} (${l.code})`
      let subLabel = `Переводов: ${l.translationsCount}`
      return { 
        value: l.id, 
        label, 
        subLabel 
      }
    })
  }

  static countryOptions(centers: Country[]): FormOption[] {
    return centers.map(c => ({ value: c.id, label: c.name }))
  }

  static centerOptions(centers: Center[]): FormOption[] {
    return centers.map(c => ({ value: c.id, label: c.name, subLabel: c.comment }))
  }

  static briefingOptions(briefings: Briefing[]): FormOption[] {
    return briefings.map(b => ({
      value: b.id,
      label: b.name,
      subLabel: b.address
    }))
  }

  static userOptions(users: User[]): FormOption[] {
    return users.map(u => ({ value: u.id, label: u.decorate().fullname(), subLabel: u.email }))
  }

  static hubOptions(hubs: Hub[]): FormOption[] {
    return hubs.map(hub => ({
      value: hub.id, 
      label: hub.name
    }))
  }

  static hubCoursesOptions(hubCourses: HubCourse[]): FormOption[] {
    return hubCourses.map(course => ({
      value: course.name, 
      label: course.name
    }))
  }

  static hubCoursesOptionsWithIds(hubCourses: HubCourse[]): FormOption[] {
    return hubCourses.map(course => ({
      value: course.id, 
      label: course.name
    }))
  }

  static contactPersonOptions(persons: User[]): FormOption[] {
    let result = []
    let contactPersonOptions = persons.map((person: User) => ({ 
      value: person.id, 
      label: person.firstname + " " +  person.lastname,
      subLabel: person.email
    }))
    if (contactPersonOptions.length > 0) {
      result = contactPersonOptions
    }
    return result
  }

  static folderSubtreeHasItems(folder: Folder, itemsIndex: { [key: number]: any[] }): boolean {
    if (itemsIndex[folder.id] && itemsIndex[folder.id].length) {
      return true
    }
    for (let subFolder of folder.subFolders) {
      if (this.folderSubtreeHasItems(subFolder, itemsIndex)) {
        return true
      }
    }
    return false
  }

  static programOptions(programs: Program[]): FormOption[] {
    return programs.map(p => ({
      value: p.id,
      label: p.name,
      subLabel: p.comment
    }))
  }

  static examOptions(exams: Exam[]): FormOption[] {
    return exams.map(e => ({
      value: e.id,
      label: e.name,
      subLabel: e.comment
    }))
  }

  static utcOffsetOptions(): FormOption[]{
    let options = range(-12, 15).map((offset: number) => {
      return { 
        value: offset, 
        label: offset == 0 ? 'GMT' : `GMT${offset > 0 ? '+' : ''}${offset}` 
      }
    })
    options.unshift({ value: null, label: '' })

    return options
  }

  static weightOptions(): FormOption[]{
    return [{
      value: "1000",
      label: "Столица"
    }, {
      value: "500",
      label: "Город федерального значиения"
    }, {
      value: "200",
      label: "Город миллионник"
    }, {
      value: "100",
      label: "Город"
    }, {
      value: "0",
      label: "Населенный пункт"
    }, {
      value: "-1",
      label: "Виртуальный город"
    }]
  }

  static interviewTypeOptions(): FormOption[] {
    return [{
      value: "interview",
      label: "Онлайн интервью"
    }, {
      value: "hub_moderation",
      label: "Модерация записей в хаб"
    }, {
      value: "unlock_base",
      label: "Разблокировка эконома"
    }]
  }

  static smsTemplateKindOptions(kinds: SmsKind[]): FormOption[] {
    return kinds.map(k => ({
      value: k.kind,
      label: k.name,
      subLabel: k.comment
    }))
  }

  static smsTemplateOptions(templates: SmsTemplate[]): FormOption[] {
    return templates.map(t => ({
      value: t.id,
      label: t.name,
      subLabel: t.comment
    }))
  }

  static cancellationReasonOptions(reasons: CancellationReason[]): FormOption[] {
    return reasons.map(r => ({
      value: r.id,
      label: r.description
    }))
  }

  static briefingAddressFilterOptions(briefings: Briefing[]): FormOption[] {
    return briefings.map(briefing => ({
      value: briefing.id,
      label: briefing.address
    }))
  }

  static mkkInspectionModeOptions(modes: { name: string, kind: string}[]): FormOption[] {
    return modes.map(m => ({
      value: m.kind,
      label: m.name
    }))
  }

  static mkkRegionOptions(regions: { name: string, id: number}[]): FormOption[] {
    return regions.map(r => ({
      value: r.id,
      label: r.name
    }))
  }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./app/core/forms/form-options-builder.ts